const axios = require("axios");
const { SecretsManagerClient, GetSecretValueCommand } = require('@aws-sdk/client-secrets-manager')
const { BaseClient } = require('../shared');

let API_URL = process.env.PLAN_MANAGER_API_URL || process.env.REACT_APP_PLAN_MANAGER_API_URL;
const facilSearch = require('./static/facilitysearch.json');

class PlanManagerClient extends BaseClient {
    constructor(
        planManagementServiceUrl = API_URL, 
        planManagementServiceSecretName = process.env.CENTIVO_PLAN_MANAGEMENT_SECRET_NAME || 'plan-management-key',
        axiosOptions
    ) {
        super(planManagementServiceUrl, planManagementServiceSecretName, axiosOptions)
    }

    async createSecureAxiosInstance(customHeaders = {}, contentType = null) {
        // Checking if the instance exists with the same headers and content type
        if (
            this.secureAxiosInstance && 
            JSON.stringify(this.secureCustomHeaders) === JSON.stringify(customHeaders) && 
            JSON.stringify(this.secureContentType) === JSON.stringify(contentType)
        ) {
            return
        }

        if (!this.secretName)
            throw new Error("'secretName' must be defined")

        const secretsManagerClient = new SecretsManagerClient()

        const { SecretString } = await secretsManagerClient.send(new GetSecretValueCommand({ SecretId: this.secretName }));

        this.secureCustomHeaders = customHeaders
        this.secureContentType = contentType

        this.secureAxiosInstance = axios.create({
            baseURL: this.url,
            headers: {
                "content-type": contentType ?? "application/json",
                Authorization: SecretString,
                ...customHeaders
            }
        });
    }

    async getAllPlans() {
        await this.createSecureAxiosInstance()

        try {
            const response = await this.secureAxiosInstance.get(`plans/getAllPlans`)

            if (response.status !== 200) {
                return null;
            }

            return response.data
        } 
        catch (error) {
            console.log("PLAN MANAGER API ERROR:", error);
            return null;
        }
    }

    /**
     * @param {*} clientId 
     * @param {*} planId 
     */
    async getPlanConfigV1(clientId, planId) {
        await this.createSecureAxiosInstance()

        try {
            const response = await this.secureAxiosInstance.get(`plans/getLegacyPlanConfig?clientId=${clientId}&planId=${planId}`);

            if (response.status !== 200) {
                return null;
            }

            return response.data
        }
        catch (error) {
            console.log("PLAN MANAGER API ERROR:", error);
            return null;
        }
    }

    /**
     * @param {*} clientId 
     * @param {*} planId  
     */
    async getPlanNetworks(clientId, planId) {
        this.createAxiosInstance()
        
        try {
            const response = await this.axiosInstance.get(`plans/getPlanNetworks?clientId=${clientId}&planId=${planId}`);

            if (response.status !== 200) {
                return null;
            }

            return response.data
        }
        catch (error) {
            console.log("PLAN MANAGER API ERROR:", error);
            return null;
        }
    }

    /**
     * @param {*} planUuid 
     * aggregate parser for planuuid
    */
    async getPlanByUuid(planUuid) {
        return await this.getPlan(planUuid.split('#')[0], planUuid.split('#')[1]);
    }

    /**
     * @param {*} clientId 
     * @param {*} planId  
     */
    async getPlan(clientId, planId) {
        await this.createSecureAxiosInstance()

        try {
            const response = await this.secureAxiosInstance.get(`plans/getInternalMedicalPlan?clientId=${clientId}&planId=${planId}`);

            if (response.status !== 200) {
                return null;
            }

            return response.data
        }
        catch (error) {
            console.log("PLAN MANAGER API ERROR:", JSON.stringify(error));
            return null;
        }
    }

    getConfigMap() {
        return {
            "EMP": "Employee Only",
            "ECH": "Employee + Children",
            "ESP": "Employee + Spouse",
            "FAM": "Family"
        }
    }

    getSpecialtyFacilitySearch() {
        return facilSearch;
    }

    /**
     * @param {*} planUuid 
     * aggregate parser for planuuid
    */
    getClientIdAndPlanIdFromPlanUuid(planUuid) {
        return {
            clientId: planUuid.split('#')[0],
            planId: planUuid.split('#')[1]
        }
    }

    /**
     * @param {*} planUuid 
     * aggregate parser for planuuid
    */
    getClientIdAndPlanIdFromMember(member) {
        return this.getClientIdAndPlanIdFromPlanUuid(member.planUuid);
    }

    async isInNetwork(network, clientId, planId) {
        try {
            const result = await this.getPlanNetworks(clientId, planId);
            if (result && result.networkIndicators) {
                if (result.networkIndicators.find(item => item.networkName != null
                    && item.networkName.toString().toLowerCase() === network.toString().toLowerCase())) {
                    return true;
                }
            }
        } catch (e) { console.log("IS IN NETWORK FAILURE:", e); return null; }
        return false;
    }

    IN_NETWORK_MEMORY = {};
    async isInNetworkMemoryCaching(network, clientId, planId) {
        const cacheKey = `${network}#${clientId}#${planId}`;
        if (this.IN_NETWORK_MEMORY[cacheKey] != null) {
            return this.IN_NETWORK_MEMORY[cacheKey];
        }

        const result = await this.isInNetwork(network, clientId, planId);
        if (result != null) {
            this.IN_NETWORK_MEMORY[cacheKey] = result;
        }
        return result;
    }

    async getDefaultNotificationConfig() {
        await this.secureAxiosInstance()

        try {
            const response = await this.secureAxiosInstance.get(`plans/getDefaultNotificationConfig`);

            if (response.status !== 200) {
                return null;
            }

            return response.data
        }
        catch (error) {
            console.log("PLAN MANAGER API ERROR:", error);
            return null;
        }
    }

}

module.exports = {
    PlanManagerClient
}
